import { inject, Injectable } from '@angular/core';
import { LanguageService } from '@mkp/shared/util-language';
import { authActions, authApiActions } from '@mkp/auth/actions';
import { LanguageMapper } from '@mkp/shared/models';
import { ZendeskService } from '@mkp/shared/util-zendesk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  appHeaderActions,
  appInitFactoryActions,
  languageEffectsActions,
} from '@mkp/core/state/actions';
import { userApiActions } from '@mkp/user/state/actions';
import { selectLoggedInUser } from '@mkp/user/state-logged-in/selectors';
import { concatMap, filter, map, takeUntil, tap } from 'rxjs/operators';

@Injectable()
export class LanguageEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly languageService = inject(LanguageService);
  private readonly zendeskService = inject(ZendeskService);

  readonly appLanguageInitialized$ = this.actions$.pipe(
    ofType(
      languageEffectsActions.appLanguageInitializedForUser,
      languageEffectsActions.appLanguageInitializedForVisitor
    )
  );

  readonly initAppLanguageForUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(appInitFactoryActions.enter),
      // wait for user details to be fetched
      concatMap(() => this.actions$.pipe(ofType(userApiActions.readLoggedInUserSuccess))),
      concatMap(
        ({
          user: {
            settings: { language },
          },
        }) => this.languageService.initAppLanguageForUser(language)
      ),
      map(({ languageIso }) =>
        languageEffectsActions.appLanguageInitializedForUser({ languageIso })
      ),
      // unsubscribe when application language is initialized
      takeUntil(this.appLanguageInitialized$)
    );
  });

  readonly initAppLanguageForVisitor$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(appInitFactoryActions.enter),
      // wait for fetch access token or initial accounts
      concatMap(() =>
        this.actions$.pipe(
          ofType(authApiActions.fetchAccessTokenFailure, authActions.loadAccountsAfterLoginFailed)
        )
      ),
      concatMap(() => this.languageService.initAppLanguageForVisitor()),
      map(({ languageIso }) =>
        languageEffectsActions.appLanguageInitializedForVisitor({ languageIso })
      ),
      // unsubscribe when application language is initialized
      takeUntil(this.appLanguageInitialized$)
    );
  });

  readonly switchAppLanguage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(appHeaderActions.switchAppLanguage),
      concatLatestFrom(() => this.store.select(selectLoggedInUser)),
      filter(
        ([{ languageIso }, user]) =>
          !user || user.settings?.language !== LanguageMapper.toCode(languageIso)
      ),
      tap(([{ languageIso }]) => this.languageService.switchAppLanguage(languageIso)),
      tap(([{ languageIso }]) => this.zendeskService.setLocale(languageIso)),
      map(([{ languageIso }, user]) =>
        user
          ? languageEffectsActions.appLanguageSwitchedForUser({
              updateUser: {
                id: user.id,
                _version: user._version,
                settings: {
                  language: LanguageMapper.toCode(languageIso),
                },
              },
              notification: false,
              redirectUrl: false,
            })
          : languageEffectsActions.appLanguageSwitchedForVisitor()
      )
    );
  });
}
